import { unwrapResponse, wrapRequest } from "../api/crypto";
import { apiHeaders, apiHeadersJson } from "../api/headers";
import { useCallback, useEffect, useState } from "react";
import { Empty, ErrorState, Loading } from "./States";
import { Meter } from "./Charts";
import { Toolbar } from "./Toolbar";
import { EM_DASH, fmtMs, humanise } from "../lib/format";

interface Verdict {
    reachable: boolean;
    ok: boolean;
    error: string | null;
    evaluated: boolean;
    verdict: { evaluation?: Record<string, any> } | null;
}

/**
 * The Tier 2 application evaluation.
 *
 * This is the platform's most complete output — entity resolution, document-set
 * completeness, cross-document agreement, compliance and risk — and until now
 * it had never been produced at all. Not because the evaluator was broken: it
 * works, and returns all of the above in one call. Nothing in the pipeline ever
 * invoked it. Ingestion writes Tier 1 into agent_results and stops, so
 * `application_intelligence` stayed empty from the day the table was created.
 *
 * So the button is deliberate. Evaluation is the one write this console
 * performs, and it is an operator action rather than an automatic side effect:
 * it costs a compliance tool call and re-reads every document in the
 * application, and firing it on page load would run on every idle refresh.
 */
export function Tier2Verdict({ applicationId }: { applicationId: string }) {
    const [state, setState] = useState<Verdict | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [running, setRunning] = useState(false);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        fetch(`/api/orchestrator/platform/applications/${applicationId}/verdict`, { headers: apiHeaders() })
            .then((r) => r.json())
            .then((b) => unwrapResponse<Verdict>(b))
            .then((d: Verdict) => setState(d))
            .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)))
            .finally(() => setLoading(false));
    }, [applicationId]);

    useEffect(load, [load]);

    const run = async () => {
        setRunning(true);
        setError(null);
        try {
            const r = await fetch(
                `/api/orchestrator/platform/applications/${applicationId}/evaluate`,
                {
                    method: "POST",
                    headers: apiHeadersJson(),
                    body: await wrapRequest({ tenant_id: "tenant_default" }),
                }
            );
            const body = r.ok ? await unwrapResponse<any>(await r.json()) : await r.json();
            if (!r.ok || body?.error) throw new Error(body?.error ?? `Evaluation failed (${r.status}).`);
            load();
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        } finally {
            setRunning(false);
        }
    };

    const ev = state?.verdict?.evaluation;

    return (
        <>
            <Toolbar
                title="Tier 2 — Application Intelligence"
                subtitle="Entity resolution, completeness, cross-document agreement, compliance and risk"
                right={
                    <button className="btn-primary" onClick={run} disabled={running}>
                        {running ? "Evaluating…" : ev ? "Re-evaluate" : "Run evaluation"}
                    </button>
                }
            />

            {error ? (
                <div className="card"><ErrorState message={error} onRetry={load} /></div>
            ) : loading ? (
                <div className="card"><Loading what="the Tier 2 verdict" /></div>
            ) : !ev ? (
                <div className="card">
                    <Empty
                        title="This application has never been evaluated"
                        hint="Tier 2 is not part of the ingestion pipeline — nothing triggers it automatically. Run it to produce an entity, completeness and risk verdict from the documents already stored."
                    />
                </div>
            ) : (
                <VerdictBody ev={ev} />
            )}
        </>
    );
}

function VerdictBody({ ev }: { ev: Record<string, any> }) {
    const comp = ev.completeness ?? {};
    const risk = ev.risk ?? {};
    const compliance = ev.compliance ?? {};
    const cross: any[] = ev.cross_document ?? [];
    const entity = ev.entity ?? {};

    const riskLevel = String(risk.level ?? "").toUpperCase();
    const riskColour =
        riskLevel === "HIGH" ? "var(--critical)"
        : riskLevel === "MEDIUM" ? "var(--serious)"
        : riskLevel === "LOW" ? "var(--good)"
        : undefined;

    const disagreements = cross.filter(
        (c) => typeof c?.agreement?.score === "number" && c.agreement.score < 0.5
    );

    return (
        <>
            <div className="tiles">
                <div className="tile">
                    <div className="tile-label">Risk</div>
                    <div className="tile-value" style={riskColour ? { color: riskColour } : undefined}>
                        {risk.level ?? EM_DASH}
                    </div>
                    <div className="tile-note">
                        {risk.score !== undefined ? `score ${risk.score}` : "no score"}
                    </div>
                </div>
                <div className="tile">
                    <div className="tile-label">Completeness</div>
                    <div className="tile-value">
                        {typeof comp.satisfied_ratio === "number"
                            ? `${Math.round(comp.satisfied_ratio * 100)}%`
                            : EM_DASH}
                    </div>
                    <div className={`tile-note${comp.complete === false ? " warn" : ""}`}>
                        {(comp.present?.length ?? 0)} of {(comp.required?.length ?? 0)} required present
                    </div>
                </div>
                <div className="tile">
                    <div className="tile-label">Compliance</div>
                    <div className="tile-value" style={{ fontSize: 18 }}>
                        <span
                            className={`pill ${
                                compliance.status === "PASS" ? "pill-good" : "pill-warning"
                            }`}
                        >
                            {compliance.status ?? "unknown"}
                        </span>
                    </div>
                    <div className="tile-note">
                        {compliance.checks?.sanctions_check === "NOT_CONFIGURED"
                            ? "no screening provider contracted"
                            : "screened"}
                    </div>
                </div>
                <div className="tile">
                    <div className="tile-label">Entity type</div>
                    <div className="tile-value" style={{ fontSize: 17 }}>
                        {entity.type_label ?? ev.entity_type ?? EM_DASH}
                    </div>
                    <div className="tile-note">
                        {comp.entity_type_determined === false ? "defaulted, not determined" : "determined"}
                    </div>
                </div>
            </div>

            {disagreements.length > 0 ? (
                <div className="notice notice-warn">
                    <strong>
                        {disagreements.length} cross-document disagreement
                        {disagreements.length === 1 ? "" : "s"}.
                    </strong>{" "}
                    The same field reads differently on different documents in this application.
                    This is the check that catches a mismatched or substituted document, so it is
                    worth resolving before the application proceeds.
                </div>
            ) : null}

            <div className="grid-2">
                <div className="card">
                    <div className="card-head bordered">
                        <span className="card-title">Resolved entity</span>
                    </div>
                    <div className="card-body">
                        <dl className="kv">
                            <dt>Canonical name</dt>
                            <dd>{entity.company?.canonical ?? EM_DASH}</dd>
                            <dt>Chosen because</dt>
                            <dd className="muted">{entity.company?.basis ?? EM_DASH}</dd>
                            <dt>Entity type</dt>
                            <dd>{entity.type_label ?? EM_DASH}</dd>
                            <dt>Type basis</dt>
                            <dd className="muted">{entity.type_basis ?? EM_DASH}</dd>
                            <dt>Jurisdiction</dt>
                            <dd>{ev.jurisdiction ?? EM_DASH}</dd>
                            <dt>Process</dt>
                            <dd>{humanise(ev.process)}</dd>
                            <dt>Document set</dt>
                            <dd className="mono">{comp.document_set ?? EM_DASH}</dd>
                        </dl>
                    </div>
                </div>

                <div className="card">
                    <div className="card-head bordered">
                        <span className="card-title">Document set</span>
                        <span className="muted">{comp.document_set ?? ""}</span>
                    </div>
                    <div className="card-body">
                        {[
                            { label: "Present", items: comp.present, colour: "var(--good)" },
                            { label: "Missing", items: comp.missing, colour: "var(--critical)" },
                            { label: "Unusable", items: comp.unusable, colour: "var(--serious)" },
                            { label: "Optional", items: comp.optional, colour: "var(--s1)" },
                        ].map((g) => (
                            <Meter
                                key={g.label}
                                label={g.label}
                                value={
                                    ((g.items?.length ?? 0) /
                                        Math.max(1, (comp.required?.length ?? 0) + (comp.optional?.length ?? 0))) *
                                    100
                                }
                                display={`${g.items?.length ?? 0}`}
                                color={g.colour}
                            />
                        ))}
                        <div className="notice" style={{ marginTop: 20, marginBottom: 0 }}>
                            {(comp.missing ?? []).length
                                ? `Missing: ${(comp.missing ?? []).map((m: any) => humanise(typeof m === "string" ? m : m?.document_type)).join(", ")}`
                                : "Every required document is present."}
                        </div>
                    </div>
                </div>
            </div>

            {cross.length > 0 ? (
                <>
                    <Toolbar
                        title="Cross-document Agreement"
                        subtitle="The same field, read from more than one document"
                    />
                    <div className="card">
                        <div className="table-wrap">
                            <table>
                                <thead>
                                    <tr>
                                        <th scope="col" className="si">Sl</th>
                                        <th scope="col">Field</th>
                                        <th scope="col" className="cell-prose">Readings</th>
                                        <th scope="col" className="num">Agreement</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {cross.map((c, i) => {
                                        const score = c?.agreement?.score;
                                        return (
                                            <tr key={i}>
                                                <td className="si">{i + 1}</td>
                                                <td className="nowrap">{humanise(c?.field ?? c?.name)}</td>
                                                <td className="cell-prose">
                                                    {Object.entries(c?.seen_as ?? c?.values ?? {}).map(
                                                        ([src, val]) => (
                                                            <div key={src}>
                                                                <span className="muted">{humanise(src)}: </span>
                                                                {String(val)}
                                                            </div>
                                                        )
                                                    )}
                                                </td>
                                                <td className="num">
                                                    {typeof score === "number" ? (
                                                        <span
                                                            className={`pill ${score < 0.5 ? "pill-critical" : score < 0.9 ? "pill-warning" : "pill-good"}`}
                                                        >
                                                            {score.toFixed(2)}
                                                        </span>
                                                    ) : (
                                                        EM_DASH
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            ) : null}

            <div className="card">
                <div className="card-head bordered">
                    <span className="card-title">Compliance checks</span>
                    <span className="muted">{fmtMs(ev.execution_time_ms)}</span>
                </div>
                <div className="card-body">
                    <dl className="kv">
                        {Object.entries(compliance.checks ?? {}).map(([k, v]) => (
                            <div key={k} style={{ display: "contents" }}>
                                <dt>{humanise(k)}</dt>
                                <dd>
                                    {v === "NOT_CONFIGURED" ? (
                                        <span
                                            className="pill pill-warning"
                                            title="No provider is contracted, so this check reports NOT_CONFIGURED rather than a clean pass."
                                        >
                                            not configured
                                        </span>
                                    ) : v === true ? (
                                        <span className="pill pill-good">pass</span>
                                    ) : v === false ? (
                                        <span className="pill pill-critical">fail</span>
                                    ) : (
                                        String(v)
                                    )}
                                </dd>
                            </div>
                        ))}
                    </dl>
                </div>
            </div>
        </>
    );
}
